import Keycloak, { KeycloakLoginOptions } from 'keycloak-js'

import { DecodedTokenType } from '../types/api'
import { AuthenticatorInterface } from '../types/authenticator'

import StorageHelper from './storageHelper'

import { ConfigType } from 'types'

const CLIENTID_STORAGE_KEY = 'CLIENTID'
const LOGOUT_URL_STORAGE_KEY = 'LOGOUT_URL'
const REDIRECT_PLACEHOLDER = 'REDIRECT_PLACEHOLDER'
const LOGIN = 'persist:login'

export type AuthenticatorConfig = ConfigType['keycloak'] & { basePath: string }

export default class Authenticator implements AuthenticatorInterface {
  keycloak: Keycloak | null
  storage: StorageHelper
  config: AuthenticatorConfig

  constructor(config: AuthenticatorConfig, storage: StorageHelper) {
    this.config = config
    this.storage = storage
    this.keycloak = this.keycloak || null
  }

  getLoginUrl() {
    this.createKeycloakClientIfNeeded()
    const loginUrl = `${window.location.origin}/${this.config.basePath}/authorize`
    this.keycloak!.init({ checkLoginIframe: false })
    return this.keycloak!.createLoginUrl({ redirectUri: loginUrl })
  }

  createKeycloakClientIfNeeded(clientId?: string): Promise<Keycloak> {
    if (!this.keycloak) {
      if (!clientId) {
        clientId = this.storage.getItem(CLIENTID_STORAGE_KEY)
        if (!clientId) {
          return Promise.reject(new Error('clientId missing. Cannot create keycloak client')
          )
        }
      }
      this.keycloak = new Keycloak({
        url: this.config.url,
        realm: this.config.realm,
        clientId,
      })
      this.storage.setItem(CLIENTID_STORAGE_KEY, clientId)
      const originalCreateLoginUrl = this.keycloak.createLoginUrl

      // Modifying the loginUrl creation so that we are able to append additional parameters to
      // the url.
      this.keycloak.createLoginUrl = (
        options?: KeycloakLoginOptions
      ): Promise<string> => {
        return originalCreateLoginUrl.call(this, options).then((url) => {
          const { queryParams } = this.config
          return (
            url +
            (queryParams!.origin ? `&origin=${queryParams!.origin}` : '') +
            (queryParams!.login_message
              ? `&login_message=${queryParams!.login_message}`
              : '')
          )
        })
      }
    }
    return Promise.resolve(this.keycloak)
  }

  authenticateUser(authUserParam) {
    this.createKeycloakClientIfNeeded(authUserParam.clientId)
    return new Promise<DecodedTokenType>((resolve, rejects) => {
      this.keycloak!
        .init({ onLoad: 'login-required', checkLoginIframe: false })
        .then(() => {
          this.storage.setItem(
            LOGOUT_URL_STORAGE_KEY,
            this.keycloak!.createLogoutUrl({
              redirectUri: REDIRECT_PLACEHOLDER,
            })
          )
          this.keycloak!
            .loadUserInfo()
            .then((userInfo) => {
              const res: DecodedTokenType = {
                ...(userInfo as DecodedTokenType),
                realm_access: this.keycloak!.realmAccess,
                resource_access: this.keycloak!.resourceAccess,
              }
              resolve(res)
            })
            .catch((error) => {
              rejects(`ERROR KC LOADUSERINFO ${error}`)
            })
        })
        .catch((error) => {
          rejects(`ERROR KC INIT ${error}`)
        })
    })
  }

  logout(redirectUrl: string): void {
    const redirectUri = redirectUrl.replace('&', '%26')
    this.createKeycloakClientIfNeeded()
    if (this.keycloak!.authenticated) {
      this.keycloak!.logout({ redirectUri })
    } else {
      window.location.href = this.storage
        .getItem(LOGOUT_URL_STORAGE_KEY)
        .replace(REDIRECT_PLACEHOLDER, redirectUri)
    }
  }

  getToken() {
    this.createKeycloakClientIfNeeded()
    return { access_token: this.keycloak!.token }
  }

  updateToken() {
    this.createKeycloakClientIfNeeded()
    return new Promise((resolve, reject) => {
      this.keycloak!
        .updateToken(10)
        .then(() => {
          resolve(undefined)
        })
        .catch(() => {
          if (this.keycloak!.authenticated) {
            this.keycloak!
              .init({ onLoad: 'check-sso' })
              .then(() => {
                resolve(undefined)
              })
              .catch(() => {
                reject('Unable to update token')
              })
          } else {
            try {
              if (this.storage.has(LOGIN)) {
                const login = this.storage.getItem(LOGIN)
                const user = JSON.parse(login.data)
                if (user && !user.orgNumber && user.userType !== 'INT') {
                  // Saknas orgNumber släng bort det vi har
                  this.storage.removeItem(LOGIN)
                }
              }
            } catch (error) {}
            this.keycloak!
              .init({ onLoad: 'login-required', checkLoginIframe: false })
              .then(() => {
                resolve(undefined)
              })
              .catch(() => {
                reject('Unable to update token')
              })
          }
        })
    })
  }
}
